import React, { useEffect } from 'react';

interface PerformanceOptimizerProps {
  enablePreloading?: boolean;
  enableResourceHints?: boolean;
  enableCriticalCSS?: boolean;
}

export const PerformanceOptimizer: React.FC<PerformanceOptimizerProps> = ({
  enablePreloading = true,
  enableResourceHints = true,
  enableCriticalCSS = true
}) => {
  useEffect(() => {
    // Preload critical resources
    if (enablePreloading) {
      const preloadResources = [
        // Critical fonts
        { href: '/fonts/inter-var.woff2', as: 'font', type: 'font/woff2', crossorigin: 'anonymous' },
        // Preload critical local images
        { href: '/images/optimized/hero/hero-0-desktop.avif', as: 'image', type: 'image/avif' },
        { href: '/images/optimized/hero/hero-0-desktop.webp', as: 'image', type: 'image/webp' },
        { href: '/images/optimized/hero/hero-0-desktop.jpg', as: 'image', type: 'image/jpeg' },
        // Critical CSS
        { href: '/src/index.css', as: 'style' }
      ];

      preloadResources.forEach(resource => {
        const link = document.createElement('link');
        link.rel = 'preload';
        link.href = resource.href;
        link.as = resource.as;
        if (resource.type) link.type = resource.type;
        if (resource.crossorigin) link.crossOrigin = resource.crossorigin;
        document.head.appendChild(link);
      });

      console.log('⚡ Critical resources preloaded');
    }

    // Add resource hints
    if (enableResourceHints) {
      const resourceHints = [
        // DNS prefetch for external domains
        { rel: 'dns-prefetch', href: '//fonts.googleapis.com' },
        { rel: 'dns-prefetch', href: '//www.googletagmanager.com' },
        
        // Preconnect to critical external resources
        { rel: 'preconnect', href: 'https://fonts.googleapis.com' },
        { rel: 'preconnect', href: 'https://fonts.gstatic.com', crossorigin: 'anonymous' }
      ];

      resourceHints.forEach(hint => {
        const link = document.createElement('link');
        link.rel = hint.rel;
        link.href = hint.href;
        if (hint.crossorigin) link.crossOrigin = hint.crossorigin;
        document.head.appendChild(link);
      });

      console.log('🔗 Resource hints added');
    }

    // Optimize images loading
    const optimizeImages = () => {
      const images = document.querySelectorAll('img[data-src]');
      const imageObserver = new IntersectionObserver((entries, observer) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const img = entry.target as HTMLImageElement;
            img.src = img.dataset.src || '';
            img.classList.remove('lazy');
            observer.unobserve(img);
          }
        });
      });

      images.forEach(img => imageObserver.observe(img));
    };

    // Defer non-critical JavaScript
    const deferNonCriticalJS = () => {
      const scripts = document.querySelectorAll('script[data-defer]');
      scripts.forEach(script => {
        const newScript = document.createElement('script');
        newScript.src = script.getAttribute('data-defer') || '';
        newScript.defer = true;
        document.body.appendChild(newScript);
      });
    };

    // Run optimizations after DOM is ready
    const timer = setTimeout(() => {
      optimizeImages();
      deferNonCriticalJS();
    }, 100);

    // Measure and report Core Web Vitals
    const measureCoreWebVitals = () => {
      // First Contentful Paint (FCP)
      new PerformanceObserver((entryList) => {
        for (const entry of entryList.getEntries()) {
          if (entry.name === 'first-contentful-paint') {
            console.log('🎯 FCP:', entry.startTime.toFixed(2) + 'ms');
          }
        }
      }).observe({ entryTypes: ['paint'] });

      // Largest Contentful Paint (LCP)
      new PerformanceObserver((entryList) => {
        const entries = entryList.getEntries();
        const lastEntry = entries[entries.length - 1];
        console.log('🎯 LCP:', lastEntry.startTime.toFixed(2) + 'ms');
      }).observe({ entryTypes: ['largest-contentful-paint'] });

      // Cumulative Layout Shift (CLS)
      let clsValue = 0;
      new PerformanceObserver((entryList) => {
        for (const entry of entryList.getEntries()) {
          if (!(entry as any).hadRecentInput) {
            clsValue += (entry as any).value;
          }
        }
        console.log('🎯 CLS:', clsValue.toFixed(4));
      }).observe({ entryTypes: ['layout-shift'] });

      // First Input Delay (FID) - now Interaction to Next Paint (INP)
      new PerformanceObserver((entryList) => {
        for (const entry of entryList.getEntries()) {
          console.log('🎯 FID:', (entry as any).processingStart - entry.startTime + 'ms');
        }
      }).observe({ entryTypes: ['first-input'] });
    };

    // Start measuring after page load
    if (document.readyState === 'complete') {
      measureCoreWebVitals();
    } else {
      window.addEventListener('load', measureCoreWebVitals);
    }

    return () => {
      clearTimeout(timer);
      window.removeEventListener('load', measureCoreWebVitals);
    };
  }, [enablePreloading, enableResourceHints, enableCriticalCSS]);

  return (
    <>
      {/* Critical CSS inlined */}
      {enableCriticalCSS && (
        <style dangerouslySetInnerHTML={{
          __html: `
            /* Critical CSS for above-the-fold content */
            .hero-section {
              background: linear-gradient(135deg, #1e3a8a 0%, #3730a3 100%);
              min-height: 100vh;
              display: flex;
              align-items: center;
            }
            
            .hero-title {
              font-size: 3.5rem;
              font-weight: 800;
              line-height: 1.1;
              color: white;
              margin-bottom: 1.5rem;
            }
            
            .hero-subtitle {
              font-size: 1.25rem;
              color: #e0e7ff;
              margin-bottom: 2rem;
              max-width: 600px;
            }
            
            .cta-button {
              background: #dc2626;
              color: white;
              padding: 1rem 2rem;
              border-radius: 0.5rem;
              font-weight: 600;
              text-decoration: none;
              display: inline-flex;
              align-items: center;
              transition: background-color 0.2s;
            }
            
            .cta-button:hover {
              background: #b91c1c;
            }
            
            /* Prevent layout shift */
            img {
              width: 100%;
              height: auto;
              display: block;
            }
            
            .lazy {
              opacity: 0;
              transition: opacity 0.3s;
            }
            
            .lazy.loaded {
              opacity: 1;
            }
          `
        }} />
      )}
      
      {/* Preload critical resources */}
      <link rel="preload" href="/fonts/inter-var.woff2" as="font" type="font/woff2" crossOrigin="anonymous" />
      
      {/* Resource hints */}
      <link rel="dns-prefetch" href="//images.unsplash.com" />
      <link rel="dns-prefetch" href="//fonts.googleapis.com" />
      <link rel="preconnect" href="https://images.unsplash.com" crossOrigin="anonymous" />
    </>
  );
};